import { useFormContext } from 'react-hook-form';
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { cx } from '@chakra-ui/utils';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  type FocusEventHandler,
  type FormEventHandler,
} from 'react';

import { useHostThemeForGateway } from '../../../hooks/useHostTheme';
import { useCreditCardFormStore } from '../../../hooks/useCreditCardFormStore';
import { generateStripeTheme } from '../../../theme/utils';
import { createStripePayment } from '../../../hooks/useStripe';
import { repackCardFormDataForStripe } from '../../../utils/repackCardFormData';

import type {
  CardExpiryElementComponent,
  CardCvcElementComponent,
  CardNumberElementComponent,
} from '@stripe/react-stripe-js';
import type { CardInputControlProps, InputType } from '../../../@types';

type InputElementType =
  | CardNumberElementComponent
  | CardCvcElementComponent
  | CardExpiryElementComponent;

const InputTypes: Record<InputType, InputElementType> = {
  card: CardNumberElement,
  cvc: CardCvcElement,
  expiry: CardExpiryElement,
};

/**
 * Map possible returnable card brand IDs with name expected by Stripe.
 */
const cardBrandNamesMap = {
  american_express: 'amex',
  americanexpress: 'amex',
  cunion: 'unionpay',
  cunionpay: 'unionpay',
  c_union_pay: 'unionpay',
  union_pay: 'unionpay',
  dan: 'dankort',
  diners: 'dinersclub',
  dinerscard: 'dinersclub',
  diners_card: 'dinersclub',
  disc: 'discover',
  hiper: 'hipercard',
  master: 'mastercard',
} as const;

/**
 * Return brands list with standardized brand names matching Stripe format.
 * @param brands Raw brands list from payment method config.
 * @returns {string[]} Brands list using standardized names.
 */
const cleanBrands = (brands: string[]) =>
  brands.map(brand => {
    const lcBrand = brand.toLowerCase() as keyof typeof cardBrandNamesMap;
    if (cardBrandNamesMap[lcBrand]) return cardBrandNamesMap[lcBrand];
    return lcBrand;
  });

/**
 * @description Input field representing a Stripe input field.
 * Used for card number, CVC, and Expiry date. Wraps Stripe Elements.
 *
 * @param {Omit<CardInputControlProps, 'onChange' | 'onBlur'> & { onChange: FormEventHandler<'input'> | ((value: any) => any); onBlur: FocusEventHandler<'input'> | ((value: any) => any); inputType: InputType; }} props - Props for the Stripe element control.
 * @param {any} ref - Forwarded ref for imperative handle.
 * @returns {JSX.Element} The Stripe input field UI.
 */
export const StripeElementControl = forwardRef(
  (
    {
      id,
      name: fieldName,
      label,
      helper,
      isDisabled,
      isRequired,
      inputType,
      // theme,
      onChange,
      onCardBrandChange,
      onBlur,
    }: Omit<CardInputControlProps, 'onChange' | 'onBlur'> & {
      onChange: FormEventHandler<'input'> | ((value: any) => any);
      onCardBrandChange: (value: string) => any;
      onBlur: FocusEventHandler<'input'> | ((value: any) => any);
      inputType: InputType;
    },
    ref,
  ) => {
    const {
      register,
      trigger,
      setValue,
      setError,
      getValues,
      clearErrors,
      formState,
    } = useFormContext();

    const { t } = useTranslation();
    const baseTheme = useHostThemeForGateway();

    const cardBrands = cleanBrands(
      useCreditCardFormStore(store => store.cardBrands),
    );

    const tempId = useId();
    const elementId = useMemo(() => id || tempId, [id, tempId]);

    const stripeTheme = useMemo(() => {
      const tm = generateStripeTheme(baseTheme.module);
      return tm[inputType];
    }, [baseTheme, inputType]);

    const registerOptions = {
      validate: {
        nativeValidation: () => formState.errors[fieldName]?.message as string,
        required: (val: string) => !isRequired || !!val || t('requiredField'),
        // cardNumber: (event: any) => {
        //   const brands = cleanBrands(cardBrands);
        //   if (event?.brand && brands?.length > 0) {
        //     if (!brands.includes(event.brand) && !(event.brand === 'unknown')) {
        //       const brandList = brands.join(', ');
        //       return `${t('unsupportedCardType')} ${t('creditCardTypesAccepted')}: ${brandList}`;
        //     }
        //   }
        //   return true;
        // },
      },
    };

    register(fieldName, { ...registerOptions });

    const isInvalid = Boolean(formState.errors[fieldName]?.message);

    /** e.g. CardNumberElement, CardCvcElement, CardExpiryElement */
    const StripeElement = InputTypes[inputType];

    useImperativeHandle(ref, () => ({
      async beforeSubmit() {
        return true;
      },
      async validate() {
        await trigger([fieldName]);
      },
    }));

    return (
      <Box>
        <FormControl
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          isRequired={isRequired}
          className={cx('InputContainer')}
        >
          <FormLabel className={cx('InputLabel')}>{label}</FormLabel>

          <StripeElement
            id={elementId}
            onReady={() => {
              // these are not supported by the stripe element
              const { height, padding } = stripeTheme.style.base;

              if (height) {
                document
                  .getElementById(elementId)
                  ?.style.setProperty('height', height);
              }
              if (padding) {
                document
                  .getElementById(elementId)
                  ?.style.setProperty('padding', padding);
              }
            }}
            onChange={(event: any) => {
              if (event.error?.message) {
                setValue(fieldName, undefined);
                setError(fieldName, {
                  type: 'validation',
                  message: event.error?.message,
                });
              } else if (event.empty) {
                setValue(fieldName, undefined);
                setError(fieldName, {
                  type: 'validation',
                  message: t('requiredField'),
                });
              } else if (
                event?.brand &&
                cardBrands?.length > 0 &&
                !cardBrands.includes(event.brand) &&
                !(event.brand === 'unknown')
              ) {
                setError(fieldName, {
                  type: 'validation',
                  message: t('unsupportedCardType'),
                });
              } else {
                setValue(fieldName, true);
                clearErrors(fieldName);
              }

              if (onCardBrandChange) onCardBrandChange(event.brand);
              if (onChange) onChange(fieldName as unknown as any);
            }}
            onBlur={() => {
              if (!getValues()[fieldName]) {
                if (!formState.errors[fieldName]?.message) {
                  setError(fieldName, {
                    type: 'validation',
                    message:
                      (formState.errors[fieldName]?.message as string) ||
                      t('requiredField'),
                  });
                }
              }
              if (onBlur) onBlur(fieldName as unknown as any);
            }}
            options={{ ...stripeTheme }}
          />

          {!isInvalid && (
            <FormHelperText className={cx('InputHelper')}>
              {helper}
            </FormHelperText>
          )}
          {isInvalid && (
            <FormErrorMessage className={cx('InputError')}>
              {formState.errors[fieldName]?.message as string}
            </FormErrorMessage>
          )}
        </FormControl>
      </Box>
    );
  },
);

/**
 * @description Ref interface for StripeElementControl imperative methods.
 */
interface StripeElementRef {
  validate: () => Promise<boolean>;
  beforeSubmit: () => Promise<boolean>;
}

/**
 * @description Stripe card element control with beforeSubmit and validate imperative methods.
 * Handles tokenization and validation for card input.
 *
 * @param {any} props - Props for the card element.
 * @param {any} ref - Forwarded ref for imperative handle.
 * @returns {JSX.Element} The Stripe card element UI.
 */
export const StripeCardElementControl = forwardRef((props: any, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const childRef = useRef<StripeElementRef>(null);
  const brandRef = useRef<string>('unknown');
  const { setValue, getValues } = useFormContext();

  useImperativeHandle(ref, () => ({
    beforeSubmit: async () => {
      if (!stripe || !elements) return false;
      const stripeTokenResponse = await createStripePayment(
        repackCardFormDataForStripe(getValues()),
        stripe,
        elements,
      );
      setValue(props.name, {
        ...stripeTokenResponse,
        cardType: brandRef.current,
      });
      return true;
    },
    validate: async () => {
      return childRef.current?.validate();
    },
  }));

  return (
    <StripeElementControl
      {...props}
      ref={childRef}
      inputType="card"
      onCardBrandChange={(brand: string) => (brandRef.current = brand)}
    />
  );
});

/**
 * @description Stripe CVC element control.
 *
 * @param {any} props - Props for the CVC element.
 * @param {any} ref - Forwarded ref for imperative handle.
 * @returns {JSX.Element} The Stripe CVC element UI.
 */
export const StripeCvcElementControl = forwardRef((props: any, ref) => {
  return <StripeElementControl {...props} ref={ref} inputType="cvc" />;
});

/**
 * @description Stripe expiry element control.
 *
 * @param {any} props - Props for the expiry element.
 * @param {any} ref - Forwarded ref for imperative handle.
 * @returns {JSX.Element} The Stripe expiry element UI.
 */
export const StripeExpiryElementControl = forwardRef((props: any, ref) => {
  return <StripeElementControl {...props} ref={ref} inputType="expiry" />;
});
